import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Property } from '@/types/property';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface PropertyWifiModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property | null;
}

const PropertyWifiModal = ({ isOpen, onClose, property }: PropertyWifiModalProps) => {
  const { updateProperty } = usePropertyMutations();
  const [network, setNetwork] = useState(property?.wifiNetwork || '');
  const [password, setPassword] = useState(property?.wifiPassword || '');
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when property changes
  useEffect(() => {
    if (property) {
      setNetwork(property.wifiNetwork || '');
      setPassword(property.wifiPassword || '');
    }
  }, [property]);

  const handleSave = async () => {
    if (!property) return;

    setIsSaving(true);
    try {
      await updateProperty.mutateAsync({
        id: property.id,
        wifiNetwork: network,
        wifiPassword: password,
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setNetwork(property?.wifiNetwork || '');
    setPassword(property?.wifiPassword || '');
    setShowPassword(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Configuración WiFi</DialogTitle>
          <p className="text-sm text-muted-foreground">{property?.shortName || property?.title}</p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="network">Red WiFi</Label>
            <Input
              id="network"
              placeholder="Nombre de la red WiFi"
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Contraseña WiFi"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyWifiModal;
